import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MoreHorizontal, NotebookText, Pencil, Plus, Trash2 } from 'lucide-react';
import * as Notebooks from 'modules/notebooks';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { formatRelativeTime } from 'lib/relativeTime';
import { NotebookDialogs, type NotebookDialog } from 'pages/Notebooks/NotebookDialogs';
import {
  BottomSheet,
  HeaderPlus,
  ListCard,
  M,
  MobileEmpty,
  MobileError,
  MobileScreen,
  MobileSkeleton,
  ScreenBody,
  ScreenHeader,
} from 'components/mobile';

/** One card of notebook rows; each row opens its workspace, and its "more" button opens a Rename / Delete sheet. */
export function NotebooksMobile() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<NotebookDialog | null>(null);
  // The notebook whose actions sheet is open.
  const [sheetTarget, setSheetTarget] = useState<Notebooks.Types.Notebook | null>(null);
  const canDelete = usePermission(PERMISSIONS.DOCUMENTS_DELETE);
  const { notebooks, isLoading, isError, refetch } = Notebooks.Hooks.useList();

  const openCreate = () => setDialog({ kind: 'create' });
  const displayTitle = (notebook: Notebooks.Types.Notebook) =>
    Notebooks.getNotebookTitle(notebook, t('notebooks.default_title'));
  const sheetTitle = sheetTarget ? displayTitle(sheetTarget) : '';

  const askFromSheet = (kind: 'rename' | 'delete') => {
    if (!sheetTarget) return;
    setSheetTarget(null);
    setDialog({ kind, notebook: sheetTarget, title: displayTitle(sheetTarget) });
  };

  const renderBody = () => {
    if (isLoading) return <MobileSkeleton />;
    if (isError) return <MobileError onRetry={() => refetch()} />;
    if (notebooks.length === 0) {
      return (
        <MobileEmpty
          icon={<NotebookText size={22} />}
          title={t('notebooks.empty_title')}
          description={t('notebooks.empty_desc')}
          action={
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex h-10 items-center gap-1.5 rounded-md px-4 text-[14px] font-semibold"
              style={{ background: M.primary, color: M.primaryInk }}
            >
              <Plus size={16} aria-hidden />
              {t('notebooks.new_notebook')}
            </button>
          }
        />
      );
    }
    return (
      <ScreenBody>
        <ListCard>
          {notebooks.map((notebook) => {
            const title = displayTitle(notebook);
            const updated = notebook.updatedAt ? formatRelativeTime(notebook.updatedAt, i18n.language) : '';
            return (
              <div key={notebook._id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => navigate(`/app/documents/${notebook._id}`)}
                  className="flex min-w-0 flex-1 items-center gap-3 py-[13px] pl-3.5 pr-1 text-left"
                >
                  <span
                    aria-hidden
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                    style={{ background: M.muted, color: M.ink2 }}
                  >
                    <NotebookText className="h-[18px] w-[18px]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-medium leading-[18px]" style={{ color: M.ink }}>
                      {title}
                    </span>
                    <span className="mt-0.5 block truncate text-[12.5px] leading-4 tabular-nums" style={{ color: M.ink3 }}>
                      {t('notebooks.sources_count', { count: notebook.documentCount })}
                      {updated && <> · {t('notebooks.updated_ago', { when: updated })}</>}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: M.chevron }} aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label={t('notebooks.more_actions', { title })}
                  onClick={() => setSheetTarget(notebook)}
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center"
                  style={{ color: M.ink3 }}
                >
                  <MoreHorizontal size={18} aria-hidden />
                </button>
              </div>
            );
          })}
        </ListCard>
      </ScreenBody>
    );
  };

  return (
    <MobileScreen>
      <div id="documents-page-container" data-tour="documents-page" className="flex min-h-0 flex-1 flex-col">
        <ScreenHeader
          title={t('notebooks.title')}
          count={isLoading ? undefined : notebooks.length}
          right={<HeaderPlus onClick={openCreate} label={t('notebooks.new_notebook')} />}
        />

        {renderBody()}
      </div>

      {/* Tapping a row opens the notebook, so this sheet carries only what a tap can't reach. */}
      <BottomSheet open={Boolean(sheetTarget)} onClose={() => setSheetTarget(null)} label={sheetTitle}>
        <p className="mb-3.5 truncate text-[15px] font-bold" style={{ color: M.ink }}>
          {sheetTitle}
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => askFromSheet('rename')}
            className="flex w-full items-center gap-2.5 rounded-md p-3 text-left"
            style={{ border: `1px solid ${M.border}` }}
          >
            <Pencil className="h-4 w-4 flex-shrink-0" style={{ color: M.ink2 }} aria-hidden />
            <span className="text-[13.5px] font-semibold" style={{ color: M.ink }}>
              {t('notebooks.rename')}
            </span>
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={() => askFromSheet('delete')}
              className="flex w-full items-center gap-2.5 rounded-md p-3 text-left"
              style={{ border: `1px solid ${M.red2}`, background: M.redTint }}
            >
              <Trash2 className="h-4 w-4 flex-shrink-0" style={{ color: M.redBright }} aria-hidden />
              <span className="text-[13.5px] font-bold" style={{ color: M.red }}>
                {t('delete')}
              </span>
            </button>
          )}
        </div>
      </BottomSheet>

      <NotebookDialogs dialog={dialog} onClose={() => setDialog(null)} />
    </MobileScreen>
  );
}

export default NotebooksMobile;
